import { PLAN_FEATURES } from './accessControl';
import { FREE_USAGE_COUNTRIES } from './usagePricing';
import { planDisplayName } from './planDisplayName';
/**
 * CAPABILITIES API - Single source of truth for frontend feature gating
 * 
 * SMB-focused approach:
 * - Plan-level capabilities define what's available
 * - Global rules for country/compliance gating
 * - Simple structure for easy frontend consumption
 */

export type CountryCode = 'US' | 'CA' | 'GB' | 'AU' | 'IN' | 'EU';

/**
 * Map libphonenumber-js ISO codes to our simplified regional CountryCode
 */
const DIRECT_COUNTRY_MAP: Record<string, CountryCode> = {
  'US': 'US',
  'CA': 'CA',
  'GB': 'GB',
  'AU': 'AU',
  'IN': 'IN',
};

// EU/EEA member states (comprehensive list)
const EU_COUNTRIES = [
  // EU member states
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA members (non-EU)
  'IS', 'LI', 'NO',
  // Other European countries often included in EU calling plans
  'CH', 'MC', 'SM', 'VA', 'AD'
];

export function normalizeCountryCode(isoCode: string): CountryCode | null {
  if (DIRECT_COUNTRY_MAP[isoCode]) {
    return DIRECT_COUNTRY_MAP[isoCode];
  }

  if (EU_COUNTRIES.includes(isoCode)) {
    return 'EU';
  }

  // Unknown country - return null to indicate not supported
  return null;
}

/**
 * The ISO-2 countries a regional bloc covers — the inverse of
 * normalizeCountryCode. Shares one list with it, so the two can't drift into
 * disagreeing about whether, say, Norway is in the EU bloc.
 */
export function isoCodesInRegion(region: CountryCode): string[] {
  if (region === 'EU') return [...EU_COUNTRIES];
  return Object.keys(DIRECT_COUNTRY_MAP).filter((iso) => DIRECT_COUNTRY_MAP[iso] === region);
}

export type VoiceCapability = {
  enabled: boolean;
  inbound: boolean;
  outbound: boolean;
  allowedCountries: CountryCode[];
  /**
   * Individual ISO-2 countries an admin has opened for THIS account, on top of
   * whatever the plan's regional blocs already allow.
   *
   * The regional model can't express "Nigeria" — normalizeCountryCode returns
   * null for anything outside the six blocs, which denies it outright — so an
   * account that legitimately needs one destination had no way to get it short
   * of a plan change. These are exceptions, not entitlements: the plan still
   * drives allowedCountries, and the price gate still applies underneath.
   */
  allowedIsoCountries: string[];
  freeCountries: CountryCode[];  // Countries included free (no credits needed)
  freeMinutesPerMonth: number | null;  // Fair use for free countries = the plan's outbound minute allowance
  bundledMinutes: number | null; // null = unlimited
  recording: boolean;
  forwarding: boolean;
  conferencing: boolean;
  transfer: boolean;
};

export type SMSCapability = {
  enabled: boolean;
  inbound: boolean;
  outbound: boolean;
  allowedCountries: CountryCode[];
  /** Per-account ISO-2 exceptions, as on VoiceCapability. */
  allowedIsoCountries: string[];
  freeCountries: CountryCode[];  // Countries included free (no credits needed)
  freeSmsPerMonth: number | null;  // Fair use for free countries = the plan's SMS allowance
  bundledMessages: number | null; // null = unlimited
  mms: boolean;
  campaigns: boolean;
  maxCampaigns: number;
};

export type PhoneNumberCapability = {
  enabled: boolean;
  maxNumbers: number;
  allowedTypes: ('local' | 'toll_free' | 'mobile')[];
  canPurchase: boolean;
  canPort: boolean;
};

export type TeamCapability = {
  enabled: boolean;
  maxMembers: number;
  roles: boolean;
  permissions: boolean;
};

export type AICapability = {
  voiceAgent: boolean;
  messageComposer: boolean;
  callSummaries: boolean;
  transcription: boolean;
};

export type IntegrationCapability = {
  enabled: boolean;
  webhooks: boolean;
  api: boolean;
  zapier: boolean;
  customIntegrations: boolean;
};

/**
 * Complete capability object returned by /api/capabilities
 */
export interface Capabilities {
  // Organization context
  organizationId: string;
  planType: string;
  planName: string;
  lifecycle: string;
  accessPosture: string;
  trialExpiresAt: string | null;
  
  // Feature capabilities
  voice: VoiceCapability;
  sms: SMSCapability;
  phoneNumbers: PhoneNumberCapability;
  team: TeamCapability;
  ai: AICapability;
  integrations: IntegrationCapability;
  
  // Analytics & reporting
  analytics: boolean;
  
  // Usage limits
  limits: {
    voiceMinutesMonthly: number | null;
    smsMonthly: number | null;
    phoneNumbers: number;
    teamMembers: number;
  };
  
  // Usage tracking (critical for frontend gating)
  usage: {
    numbersIncluded: number;  // Max numbers allowed by plan
    numbersInUse: number;      // Current numbers in use
    teamMembersIncluded: number; // Max team members allowed
    teamMembersInUse: number;    // Current team members
  };
  
  // Account status
  balanceOK: boolean;           // Whether account has sufficient balance/not suspended for billing
  hasMultipleUsers: boolean;    // Whether organization has >1 active user
  
  // Global restrictions (compliance/risk)
  restrictions: {
    blockedCountries: CountryCode[];
    requiresCompliance: boolean;
    dailySpendCap: number | null;
  };
}

/**
 * Global country rules (platform-wide defaults)
 */
export const GLOBAL_COUNTRY_RULES = {
  // Countries always available for basic plans (Free Calls & SMS)
  domesticCountries: ['US', 'CA', 'GB'] as CountryCode[],
  
  // Countries requiring higher plans
  internationalCountries: ['AU', 'IN', 'EU'] as CountryCode[],
  
  // High-risk countries (blocked by default)
  blockedCountries: [] as CountryCode[],
};

/**
 * Map subscription plan to capabilities
 * Converts subscriptionPlans.metadata/limitations into Capabilities format
 */
/**
 * Keep only well-formed, de-duplicated ISO-2 codes.
 *
 * Used both when an admin saves an allowlist and when one is read back, so a
 * malformed value written directly to the database degrades to "no exceptions"
 * rather than throwing and taking outbound calling down for the account.
 */
export function sanitizeIsoCountries(raw: any): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  for (const c of raw) {
    const code = String(c ?? '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) seen.add(code);
  }
  return Array.from(seen).sort();
}

/**
 * Whether plan `planId` includes the AI receptionist. Unknown ids (custom or
 * admin-made plans) keep the old default of true; known ids follow
 * PLAN_FEATURES, so Lite is false and Starter/Growth are true.
 */
export function planListsAiVoice(planId?: string | null): boolean {
  const features = planId ? PLAN_FEATURES[planId] : undefined;
  return features ? features.includes('aiVoice') : true;
}

export function planToCapabilities(
  plan: any, 
  organization: any,
  usage?: {
    numbersInUse?: number;
    teamMembersInUse?: number;
  }
): Capabilities {
  const planType = plan.type || 'trial';
  const limitations = plan.limitations || {};
  const metadata = plan.metadata || {};
  
  // Usage data with defaults
  const numbersInUse = usage?.numbersInUse ?? 0;
  const teamMembersInUse = usage?.teamMembersInUse ?? 1;
  
  // Determine allowed countries based on plan metadata or fallback to defaults
  // Empty arrays should fallback to defaults (not block all countries)
  const voiceCountries: CountryCode[] = (metadata.allowedVoiceCountries && metadata.allowedVoiceCountries.length > 0)
    ? metadata.allowedVoiceCountries as CountryCode[]
    : (planType === 'trial' 
        ? GLOBAL_COUNTRY_RULES.domesticCountries
        : [...GLOBAL_COUNTRY_RULES.domesticCountries, ...GLOBAL_COUNTRY_RULES.internationalCountries]);
  
  const smsCountries: CountryCode[] = (metadata.allowedSmsCountries && metadata.allowedSmsCountries.length > 0)
    ? metadata.allowedSmsCountries as CountryCode[]
    : (planType === 'trial'
        ? GLOBAL_COUNTRY_RULES.domesticCountries
        : [...GLOBAL_COUNTRY_RULES.domesticCountries, ...GLOBAL_COUNTRY_RULES.internationalCountries]);
  
  // Determine free countries (included in plan, no credits needed)
  // Default: Trial gets US/CA only, Paid plans get US/CA/GB
  // IMPORTANT: Free countries must be a subset of allowed countries
  // Paid default = the fair-use included countries (shared/usagePricing,
  // editable at Admin › Revenue › Fair use — server/lib/fairUsePolicy), the
  // same list the charging engine uses. Its in-code default equals
  // GLOBAL_COUNTRY_RULES.domesticCountries (US, CA, GB).
  const rawFreeVoiceCountries: CountryCode[] = (metadata.freeVoiceCountries && metadata.freeVoiceCountries.length > 0)
    ? metadata.freeVoiceCountries as CountryCode[]
    : (planType === 'trial' 
        ? ['US', 'CA'] as CountryCode[]
        : [...FREE_USAGE_COUNTRIES] as CountryCode[]);
  
  const rawFreeSmsCountries: CountryCode[] = (metadata.freeSmsCountries && metadata.freeSmsCountries.length > 0)
    ? metadata.freeSmsCountries as CountryCode[]
    : (planType === 'trial'
        ? ['US', 'CA'] as CountryCode[]
        : [...FREE_USAGE_COUNTRIES] as CountryCode[]);
  
  // Ensure free countries are subset of allowed countries
  const freeVoiceCountries = rawFreeVoiceCountries.filter(c => voiceCountries.includes(c));
  const freeSmsCountries = rawFreeSmsCountries.filter(c => smsCountries.includes(c));
  
  // Fair use = the plan allowance (owner decision 2026-10-23). "Excessive use"
  // is use past the plan's included minutes / SMS, which is then billed as
  // overage (or blocked where the plan sells none). There is no separate
  // fair-use cap any more: the old freeDomesticMinutesPerMonth /
  // freeDomesticSmsPerMonth keys (the admin form suggested 1,000 min / 500 SMS)
  // are no longer read. null = the plan sets no allowance (uncapped).
  const freeVoiceMinutesPerMonth = limitations.outboundMinutes ?? null;
  const freeSmsPerMonth = limitations.smsPerMonth ?? null;

  // The trial is INBOUND: the AI answers calls and web callbacks; it does not
  // dial out and it does not text. Keyed on the org's lifecycle, not on the
  // plan's limitations, because the plan row is the wrong place for a policy
  // this important — limecall-trial ships with smsPerMonth: 50, which quietly
  // granted every trial fifty texts and disabled the gates written to stop them.
  // Deriving it here also means a trial can sit on a REAL plan (previewing what
  // they'd get) without that plan's allowances leaking into the trial.
  const trialing = (organization.lifecycle || '') === 'trialing';

  // Every `planType !== 'trial'` check below is gated on `trialing` as well.
  // A trial now sits on the REAL plan the customer picked, so that Stripe
  // charges the right amount when it ends — which means plan.type is
  // 'subscription' and every feature keyed only on the plan type unlocked for
  // the duration of the trial. canPurchase and canPort were already gated this
  // way; the rest were not.

  return {
    organizationId: organization.id,
    planType: planType,
    // Display label (metadata.displayName, else name); never compared on.
    planName: planDisplayName(plan) || 'Trial',
    lifecycle: organization.lifecycle || 'trialing',
    accessPosture: organization.accessPosture || 'normal',
    trialExpiresAt: organization.trialExpiresAt,
    
    voice: {
      enabled: true,
      inbound: true,
      outbound: !trialing && limitations.outboundMinutes !== 0,
      allowedCountries: voiceCountries,
      // Plan grants regions; individual countries are an admin decision per
      // account, applied from organization_capability_overrides.
      allowedIsoCountries: [],
      freeCountries: freeVoiceCountries,
      freeMinutesPerMonth: freeVoiceMinutesPerMonth,
      bundledMinutes: limitations.outboundMinutes || null,
      // Call recording is on every plan, trials included. Only orgs with no
      // plan (the 'free' fallback for cancelled/expired accounts) go without.
      recording: planType !== 'free',
      forwarding: true,
      conferencing: !trialing && planType !== 'trial',
      transfer: !trialing && planType !== 'trial',
    },
    
    sms: {
      // `enabled` stays plan-derived so RECEIVING keeps working on trial — only
      // the outbound direction is withheld.
      enabled: limitations.smsPerMonth !== 0,
      inbound: true,
      outbound: !trialing && limitations.smsPerMonth !== 0,
      allowedCountries: smsCountries,
      allowedIsoCountries: [],
      freeCountries: freeSmsCountries,
      freeSmsPerMonth: freeSmsPerMonth,
      bundledMessages: limitations.smsPerMonth || null,
      mms: !trialing && planType !== 'trial',
      campaigns: limitations.campaigns > 0,
      maxCampaigns: limitations.campaigns || 0,
    },
    
    phoneNumbers: {
      enabled: true,
      maxNumbers: limitations.localNumbers || 1,
      allowedTypes: (trialing || planType === 'trial') ? ['local'] : ['local', 'toll_free'],
      // Keyed on the lifecycle as well as the plan: a trial previewing a real
      // plan must not inherit that plan's right to buy numbers.
      canPurchase: !trialing && planType !== 'trial',
      canPort: !trialing && planType !== 'trial',
    },
    
    team: {
      enabled: limitations.teamMembers > 1,
      maxMembers: limitations.teamMembers || 1,
      roles: !trialing && planType !== 'trial',
      permissions: !trialing && planType !== 'trial',
    },
    
    ai: {
      // On for every plan, off only where a plan says so explicitly.
      //
      // This read `metadata.aiVoiceAgent || false`, so a plan row without the
      // flag had no AI receptionist. Setting the flag on the plan definitions
      // does not help until the catalogue is re-seeded, which left the feature
      // locked for everyone despite the decision to include it in every plan.
      // What a plan gets is limited by its aiTalkMinutes pool, not by whether
      // it can see the feature at all.
      //
      // Exception: the AI receptionist is sold on Starter and Growth only, so a
      // plan id that shared/accessControl PLAN_FEATURES knows and lists without
      // 'aiVoice' (Lite, incl. the legacy starter_yearly id) reports false.
      // The paid add-on (capabilitiesService) can still switch it on.
      // A trial keeps it (answering calls is what the trial is for).
      voiceAgent: metadata.aiVoiceAgent !== false && (trialing || planType === 'trial' || planListsAiVoice(plan.id)),
      messageComposer: !trialing && planType !== 'trial',
      callSummaries: !trialing && planType !== 'trial',
      transcription: !trialing && planType !== 'trial',
    },
    
    integrations: {
      enabled: !trialing && planType !== 'trial',
      webhooks: !trialing && planType !== 'trial',
      api: metadata.apiAccess || false,
      zapier: !trialing && planType !== 'trial',
      customIntegrations: planType === 'enterprise',
    },
    
    // Analytics is on every plan, including the trial. It was locked behind
    // the paid tiers entirely, so a trial — and a Lite customer — saw a
    // marketing panel where the product should be. What each plan gets is
    // limited by how much data it has, not by a gate.
    analytics: true,
    
    limits: {
      voiceMinutesMonthly: limitations.outboundMinutes || null,
      smsMonthly: limitations.smsPerMonth || null,
      phoneNumbers: limitations.localNumbers || 1,
      teamMembers: limitations.teamMembers || 1,
    },
    
    usage: {
      numbersIncluded: limitations.localNumbers || 1,
      numbersInUse: numbersInUse,
      teamMembersIncluded: limitations.teamMembers || 1,
      teamMembersInUse: teamMembersInUse,
    },
    
    balanceOK: organization.accessPosture !== 'locked' && 
               organization.accessPosture !== 'suspended' &&
               organization.lifecycle !== 'canceled',
    hasMultipleUsers: teamMembersInUse > 1,
    
    restrictions: {
      blockedCountries: GLOBAL_COUNTRY_RULES.blockedCountries,
      requiresCompliance: organization.country === 'US',
      dailySpendCap: null, // Future: org-level overrides
    },
  };
}

/**
 * Helper to check if a specific country is allowed for voice/SMS
 */
export function canCallCountry(capabilities: Capabilities, country: CountryCode): boolean {
  return capabilities.voice.enabled && 
         capabilities.voice.outbound &&
         capabilities.voice.allowedCountries.includes(country);
}

export function canSmsCountry(capabilities: Capabilities, country: CountryCode): boolean {
  return capabilities.sms.enabled &&
         capabilities.sms.outbound &&
         capabilities.sms.allowedCountries.includes(country);
}

/**
 * Can this account call this ISO-2 destination?
 *
 * Prefer this over canCallCountry at any gate that starts from a real phone
 * number. It answers the same question but also honours the per-account
 * exceptions an admin has granted — including countries the regional model
 * cannot represent, where normalizeCountryCode returns null and the regional
 * check would deny outright.
 *
 * NOT a fraud decision. The destination price gate in outboundGuard runs
 * independently and still refuses premium-rate ranges inside an allowed
 * country — enabling a country must never re-open the toll-fraud hole.
 */
export function canCallIsoCountry(capabilities: Capabilities, isoCode: string): boolean {
  if (!capabilities.voice.enabled || !capabilities.voice.outbound) return false;
  const iso = String(isoCode ?? '').trim().toUpperCase();
  if (!iso) return false;
  if (capabilities.voice.allowedIsoCountries?.includes(iso)) return true;
  const region = normalizeCountryCode(iso);
  return !!region && capabilities.voice.allowedCountries.includes(region);
}

/** SMS equivalent of canCallIsoCountry. */
export function canSmsIsoCountry(capabilities: Capabilities, isoCode: string): boolean {
  if (!capabilities.sms.enabled || !capabilities.sms.outbound) return false;
  const iso = String(isoCode ?? '').trim().toUpperCase();
  if (!iso) return false;
  if (capabilities.sms.allowedIsoCountries?.includes(iso)) return true;
  const region = normalizeCountryCode(iso);
  return !!region && capabilities.sms.allowedCountries.includes(region);
}

/**
 * What the dialer's country picker should offer: the domestic countries, plus
 * exactly the countries an admin enabled for this account.
 *
 * Deliberately NOT the full permission surface. Plans carry no
 * allowedVoiceCountries of their own, so planToCapabilities falls back to
 * domestic + international for every paid account — which includes the 'EU'
 * bloc, and expanding that put ~35 countries in the picker for accounts whose
 * own UI says "US, Canada & UK". A picker is a shortlist; showing everything
 * technically permitted made the common case (dial a US number) worse.
 *
 * This narrows what's OFFERED, not what's allowed. A full international number
 * typed with its own country code still goes through — the dialer uses the
 * typed code — and the backend gates decide from allowedCountries as before.
 * So the way to get France into someone's picker is to enable FR for them,
 * which is the point of the feature.
 */
export function dialerCountriesFor(capabilities: Capabilities, channel: 'voice' | 'sms'): string[] {
  const cap = channel === 'voice' ? capabilities.voice : capabilities.sms;
  if (!cap.enabled || !cap.outbound) return [];
  // Domestic first and in their usual order (US, CA, GB) so the common case is
  // the first thing in the list — sorting alphabetically would put Canada ahead
  // of the US and change which one a fresh picker lands on. Admin-enabled
  // countries follow, sorted, so a long list stays scannable.
  const domestic: string[] = [];
  for (const iso of GLOBAL_COUNTRY_RULES.domesticCountries) {
    if (cap.allowedCountries.includes(iso)) domestic.push(iso);
  }
  const extras = Array.from(new Set(cap.allowedIsoCountries ?? []))
    .filter((iso) => !domestic.includes(iso))
    .sort();
  return [...domestic, ...extras];
}

/**
 * Every ISO-2 destination this account may call. Expands the plan's regional
 * blocs and folds in the admin's exceptions — the full permission surface,
 * which is wider than what the picker offers (see dialerCountriesFor).
 */
export function allowedIsoCountriesFor(capabilities: Capabilities, channel: 'voice' | 'sms'): string[] {
  const cap = channel === 'voice' ? capabilities.voice : capabilities.sms;
  if (!cap.enabled || !cap.outbound) return [];
  const out = new Set<string>(cap.allowedIsoCountries ?? []);
  for (const region of cap.allowedCountries) {
    for (const iso of isoCodesInRegion(region)) out.add(iso);
  }
  return Array.from(out).sort();
}

/**
 * Helper to check if a country is FREE (no credits needed) for voice/SMS
 */
export function isVoiceCountryFree(capabilities: Capabilities, country: CountryCode): boolean {
  return capabilities.voice.freeCountries.includes(country);
}

export function isSmsCountryFree(capabilities: Capabilities, country: CountryCode): boolean {
  return capabilities.sms.freeCountries.includes(country);
}

/**
 * Get fair usage limit status for free countries
 */
export function getVoiceFairUsageLimit(capabilities: Capabilities): { limit: number | null; isUnlimited: boolean } {
  const limit = capabilities.voice.freeMinutesPerMonth;
  return { limit, isUnlimited: limit === null };
}

export function getSmsFairUsageLimit(capabilities: Capabilities): { limit: number | null; isUnlimited: boolean } {
  const limit = capabilities.sms.freeSmsPerMonth;
  return { limit, isUnlimited: limit === null };
}
